import NewsLandingPage from "@components/news-landing-page";
import { PageNewsLandingComp } from "@graphql/generated/page";
import { GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import { loadTranslation } from "@lib/utils";
import { t, Trans } from "@lingui/macro";
import { toast } from "react-toastify";

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const translation = await loadTranslation(
    locale,
    process.env.NODE_ENV == "production"
  );

  return { props: { translation, locale } };
};

export const kTopicsList = [
  {
    name: <Trans>Latest ⚡</Trans>,
    key: "latest",
    emoji: "",
    seoTitle: t`Latest News Today - Lumi News`,
    seoDescription: t`Get the latest, breaking news and top headlines from New Straits Times (NST), Free Malaysia Today (FMT), SAYS, The Vibes, The Rakyat Post, and more here.`,
  },
  // {
  //   key: "GE15",
  //   name: <Trans>GE15</Trans>,
  //   emoji: "🗳",
  //   seoTitle: t`GE15 News And Results - Lumi News`,
  //   seoDescription: t`GE15 live tally, news, hot seats, candidates, and election campaigns on the Lumi News app. Stay up to date and get real-time GE15 election results.`,
  // },
  {
    key: "Trending",
    name: <Trans>Trending</Trans>,
    emoji: "🔥",
    seoTitle: t`Trending News in Malaysia - Lumi News`,
    seoDescription: t`What's trending and hot right now? Discover all the trending and viral stories in Malaysia today.`,
  },
  {
    name: <Trans>Good News 🎉</Trans>,
    key: "Good_News",
    emoji: "",
    bgActiveClass: "bg-green",
    seoTitle: t`Good News in Malaysia - Lumi News`,
    seoDescription: t`Read all the good news in Malaysia today. Inspiring, wholesome, optimistic, humorous, and heartwarming news from Malaysia's top publishers.`,
  },
  {
    key: "News",
    name: <Trans>News</Trans>,
    emoji: "☕",
    seoTitle: t`News - Lumi News`,
    seoDescription: t`Get all the important news and updates on national, regional, and world issues from Malaysia's top publishers.`,
  },
  {
    key: "World",
    name: <Trans>World</Trans>,
    emoji: "🌎",
    seoTitle: t`World News - Lumi News`,
    seoDescription: t`Get the latest international news and events from around the world, reported by Malaysia's top publishers.`,
  },
  {
    key: "Politics",
    name: <Trans>Politics</Trans>,
    emoji: "♟️",
    seoTitle: t`Politics - Lumi News`,
    seoDescription: t`Get the latest news on Malaysian politics and stay on top of the political situation in Malaysia.`,
  },
  {
    key: "Lifestyle",
    name: <Trans>Lifestyle</Trans>,
    emoji: "🏝️",
    seoTitle: t`Lifestyle - Lumi News`,
    seoDescription: t`Guides and exclusive features on food, travel, style, entertainment, health, and more from Malaysia's top publishers.`,
  },
  {
    key: "Tech",
    name: <Trans>Tech</Trans>,
    emoji: "📱",
    seoTitle: t`Tech - Lumi News`,
    seoDescription: t`Get the latest technology news, buying guides, and gadget reviews from Malaysia's top publishers.`,
  },
  {
    key: "Sports",
    name: <Trans>Sports</Trans>,
    emoji: "🏸",
    seoTitle: t`Sports - Lumi News`,
    seoDescription: t`Get the latest sports news, scores, and stay up-to-date on your favourite sports and teams, reported by Malaysia's top publishers.`,
  },
];

const News: PageNewsLandingComp = () => {
  const { query, pathname } = useRouter();
  const router = useRouter();
  const locale = router.locale || router.defaultLocale;
  const seoLocale = "/" + locale;
  const topic = kTopicsList.find((topic) => topic.key == query.topic);

  if (query.contentNotFound == "true") {
    const params = new URLSearchParams(query as Record<string, any>);
    params.delete("contentNotFound");

    toast(
      <span className="font-sen">
        Whoops, the content is unavailable. Try something else.
      </span>,
      {
        position: "bottom-center",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        className: "min-w-[350px] !text-white !font-sen !bg-[#00C1B6]",
        theme: "light",
      }
    );

    router.replace(
      { pathname, query: params.toString() }, // Update with the new query string
      undefined,
      { shallow: true } // Shallow routing prevents a full page reload
    );
  }

  // make sure there's a fallback if no topic found
  const { seoTitle, seoDescription } = topic || {
    seoTitle: t`News in Malaysia - Lumi News`,
    seoDescription: t`See the latest news from New Straits Times (NST), Free Malaysia Today (FMT), SAYS, The Vibes, The Rakyat Post, and more trusted news sites, all in one place.`,
  };

  return (
    <>
      <NextSeo
        title={seoTitle}
        description={seoDescription}
        openGraph={{
          url: `https://luminews.my${
            seoLocale.includes("en") ? "" : seoLocale
          }${router.asPath}`,
          title: seoTitle,
          description: seoDescription,
        }}
      />

      <NewsLandingPage topics={kTopicsList} />
    </>
  );
};

export default News;
